import PropTypes from "prop-types";
import React from "react";

import { classNames } from "@swa-ui/string";

import { AnimateSvgElement } from "../AnimateSvgElement";
import { getColorVariable } from "../getColorVariable";
import { Transition, transitionPropTypes } from "../Transition";
import styles from "./AnimateSvg.module.scss";

/**
 * AnimateSvg renders an SVG element with given definition for the path, circle, or other element.
 */
export const AnimateSvg = (props) => {
  const { animate, className, element, initial, style, svgElementProps, transition } = props;

  return (
    <Transition {...getProps()}>
      <AnimateSvgElement {...getSvgElementProps()} />
    </Transition>
  );

  function getProps() {
    return {
      animate,
      "aria-label": props["aria-label"],
      className: classNames(styles.animateSvg, className),
      component: "svg",
      initial: {
        ...initial,
        ...(initial?.fill && { fill: getColorVariable(initial.fill) }),
        ...(initial?.stroke && { stroke: getColorVariable(initial.stroke) }),
      },
      style,
      transition,
    };
  }

  function getSvgElementProps() {
    const elementProps = {
      animate: { ...svgElementProps.animate },
      element,
      initial: { ...svgElementProps },
      style: { ...svgElementProps.style },
      transition: { ...svgElementProps.transition },
    };

    delete elementProps.initial.animate;
    delete elementProps.initial.initial;
    delete elementProps.initial.style;
    delete elementProps.initial.transition;

    return elementProps;
  }
};

AnimateSvg.displayName = "AnimateSvg";

AnimateSvg.propTypes = {
  /** Values to transition. See Transition component for more information. */
  animate: transitionPropTypes.animate,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /** Name of SVG element to be rendered in the SVG container. Element would be like "path" or "circle". */
  element: PropTypes.string,

  /** Values to set on SVG element initially. See Transition component for more information. */
  initial: transitionPropTypes.initial,

  /** This object provides a way to add CSS values in addition to the properties that will be transformed. */
  style: PropTypes.object,

  /** Any props that would apply to SVG elements like path or circle. */
  svgElementProps: PropTypes.object,

  /** Values to transition. See Transition component for more information. */
  transition: transitionPropTypes.transition,
};
